import { Controller } from "@hotwired/stimulus";
import { actionSource } from "../utils/action_source";
import { hasModifierChord, isReservedArrowChord, logicalArrowStep } from "../utils/arrow_step";
import { validSelector } from "../utils/declared_value";
import { ListenerSet } from "../utils/listener_set";
import { MorphRenderWatcher } from "../utils/morph_render_watcher";
import { RovingTabindex, rovingMove } from "../utils/roving_tabindex";
import { readLocalStorage, writeLocalStorage } from "../utils/safe_storage";
import { type StateReason, stateReasonFor } from "../utils/state_reason";
import { targetSelector } from "../utils/target_selector";

/** The three selectable modes; `system` follows the OS `prefers-color-scheme`. */
type ThemeMode = "light" | "dark" | "system";
/** The two effective (resolved) themes applied to the root. */
type ResolvedTheme = "light" | "dark";

const MODES: readonly ThemeMode[] = ["light", "dark", "system"];
const isMode = (value: unknown): value is ThemeMode =>
  typeof value === "string" && (MODES as readonly string[]).includes(value);

/** The mode a declaration falls back to when it cannot be read as one. */
const DEFAULT_MODE: ThemeMode = "system";
/** The state-hook target a declaration falls back to when it cannot be parsed. */
const DEFAULT_TARGET = "html";

/**
 * Headless **theme / color-scheme toggle** — persists a light/dark/system choice to
 * `localStorage`, follows the OS setting while in `system`, and reflects the
 * effective theme onto the root for the consumer's CSS. Ships no colors; only state
 * hooks.
 *
 * Toggle resolves persisted preference before the declared mode, including system
 * preference, so it inverts the effective current theme.
 *
 * Two markup contracts (identifier: `stimeo--theme`):
 *
 * Canonical 3-value radiogroup (use this when `system` is offered):
 *   <div data-controller="stimeo--theme" data-stimeo--theme-mode-value="system"
 *        role="radiogroup" aria-label="Theme">
 *     <button data-stimeo--theme-target="option" role="radio"
 *             data-action="click->stimeo--theme#set"
 *             data-value="light">Light</button>
 *     <button data-stimeo--theme-target="option" role="radio"
 *             data-action="click->stimeo--theme#set"
 *             data-value="dark">Dark</button>
 *     <button data-stimeo--theme-target="option" role="radio"
 *             data-action="click->stimeo--theme#set"
 *             data-value="system">System</button>
 *   </div>
 *
 * Auxiliary 2-value toggle (light↔dark only — `system` is not representable):
 *   <button data-controller="stimeo--theme" data-action="click->stimeo--theme#toggle"
 *           aria-pressed="false">Dark mode</button>
 *
 * `change` dispatches `{ mode, resolved, reason }` when a selection or the OS preference moves
 * one of the two. `reconcile` dispatches `{ mode, resolved }` when a `mode`
 * declaration that changes after connect moves one of them — the declaration
 * itself, or a stored choice that outranks it — or a `storageKey` change brings the
 * choice saved under the new key. Connecting is silent.
 *
 * @remarks
 * Behavior only — the actual palette is the consumer's CSS keyed off `data-theme`
 * on the root. It applies `data-theme` (the *resolved* light/dark) and a matching
 * `color-scheme` to the `target` element (`<html>` by default), keeps the radiogroup
 * `aria-checked` + roving tabindex (APG radio) or the single button's `aria-pressed`
 * in sync. Arrow keys move focus and the selection across the radiogroup as the APG radio
 * pattern requires, and Home/End move both to the first/last option; applying a theme never
 * moves focus. The `prefers-color-scheme` listener is attached on
 * `connect()` and removed on `disconnect()` (Turbo included). FOUC avoidance for the
 * very first paint is an inline `<head>` snippet, not this controller. A `mode`
 * declaration rewritten after connect is followed, but never over a stored choice
 * ({@link ThemeController.modeValueChanged}), and so is a `storageKey`
 * ({@link ThemeController.storageKeyValueChanged}).
 *
 * Every declaration is validated where it enters, and an unreadable one falls back
 * to that Value's default rather than taking the widget with it: a `mode` outside
 * the three modes reads as `system`, and a `target` that is not a parsable selector
 * reads as `html`. An option whose `data-value` is outside the three can be focused
 * but never becomes the selection. Every option matching the selected mode is
 * checked, and the first matching option keeps the Tab stop. When no option
 * matches, none is checked and the first option keeps the Tab stop.
 *
 * The radiogroup stays one Tab stop through `RovingTabindex`, re-derived
 * whenever an option enters or leaves, so a set rendered after connect or swapped
 * by a Turbo morph carries the same single stop as any other.
 */
export class ThemeController extends Controller<HTMLElement> {
  static override targets = ["option"];
  static override values = {
    mode: { type: String, default: DEFAULT_MODE },
    storageKey: { type: String, default: "stimeo-theme" },
    target: { type: String, default: DEFAULT_TARGET },
  };
  static actions = ["set", "toggle"] as const;
  static events = ["change", "reconcile"] as const;

  declare readonly optionTargets: HTMLElement[];
  declare readonly hasOptionTarget: boolean;

  declare modeValue: string;
  declare storageKeyValue: string;
  declare targetValue: string;

  /** The OS dark-mode query, watched so `system` tracks live changes. */
  #media: MediaQueryList | null = null;

  /**
   * The listeners of one connection — the query each connection takes and the keys on
   * the element — released together on `disconnect()`.
   */
  readonly #listeners = new ListenerSet();

  /** Gate for the target and `mode` callbacks, which Stimulus runs before `connect()`. */
  #connected = false;

  /** The `target` declaration after validation; the default when unparsable. */
  #targetSelector = DEFAULT_TARGET;

  /** The storage key last read, so a `storageKey` callback naming it again reads nothing. */
  #storageKeyRead: string | null = null;

  /** Owns the single Tab stop across the option set (APG radiogroup). */
  readonly #roving = new RovingTabindex(() => this.optionTargets);

  /**
   * The pair on screen as last reported or re-seeded, so a move can be told from a
   * repeat. Neither side is readable after the fact — assigning the Value updates the
   * mode before any comparison, and the OS query has already flipped by the time it
   * notifies — so what was reported has to be kept rather than recomputed.
   */
  #published: { mode: ThemeMode; resolved: ResolvedTheme } = {
    mode: DEFAULT_MODE,
    resolved: "light",
  };

  /** Re-resolves while in `system` mode when the OS preference flips. */
  readonly #onMediaChange = (): void => {
    // Only `system` follows the OS; an explicit mode already decided the answer.
    if (this.#mode !== "system") return;
    this.#commit("media");
  };

  /** Arrow-key navigation for the radiogroup (APG radio pattern), plus Home/End. */
  readonly #onKeydown = (event: KeyboardEvent): void => {
    // A descendant widget that already claimed the key (a grabbed drag handle, a
    // nested menu) must not ALSO act on it — composition depends on this yield.
    if (event.defaultPrevented) return;
    if (isReservedArrowChord(event)) return;
    // Resolved once per keydown: every `optionTargets` access re-queries the scope.
    const options = this.optionTargets;
    const target = event.target as HTMLElement | null;
    const current = options.indexOf(target as HTMLElement);
    if (current === -1) return;

    let next = current;
    // Logical, not physical. The helper reverses only the horizontal pair, so
    // folding Down/Up into the same branch stays correct.
    const step = logicalArrowStep(event.key, this.element);
    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
      case "ArrowUp":
      case "ArrowLeft":
        next = rovingMove(current, options.length, step, "wrap");
        break;
      case "Home":
      case "End":
        // Control+Home jumps the document, not the widget.
        if (hasModifierChord(event)) return;
        next = event.key === "Home" ? 0 : options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const option = options[next];
    if (!option) return;
    option.focus();
    const mode = this.#optionMode(option);
    // An option outside the three modes can hold focus, but the selection does
    // not follow it there.
    if (mode) this.#setMode(mode);
  };

  readonly #morph = new MorphRenderWatcher(() => {
    this.#applyTheme();
    this.#reconcileControls();
  });

  override connect(): void {
    this.#morph.observe(this.element);
    this.#storageKeyRead = this.storageKeyValue;
    const stored = this.#readStored();
    if (stored) this.modeValue = stored;

    this.#media = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
    if (this.#media) this.#listeners.add(this.#media, "change", this.#onMediaChange);
    this.#listeners.add(this.element, "keydown", this.#onKeydown);

    this.#connected = true;
    this.#applyTheme();
    this.#syncControls();
    // Connecting is not a change: seed the baseline instead of announcing one.
    this.#published = this.#current;
  }

  override disconnect(): void {
    this.#morph.disconnect();
    this.#connected = false;
    this.#listeners.dispose();
  }

  /** Validates the `target` declaration once, so the render path never parses. */
  targetValueChanged(): void {
    this.#targetSelector = validSelector(this.element, this.targetValue, DEFAULT_TARGET);
    this.#morph.schedule();
  }

  /**
   * Re-renders for a `mode` declaration changed after connect — a Turbo morph
   * re-rendering the server's markup over the live element, or a script.
   *
   * A stored choice outranks the declaration: a Value that disagrees with it is written
   * back to it, so a morph that brings the server's default never overrides what the
   * user saved. Without one, the declaration is applied. Either way storage is left
   * alone, and a pair on screen that moved is reported as `reconcile`, not `change` —
   * the page moved the declaration, not a selection. The reported pair is re-seeded
   * first, so the next selection is compared with what is on screen.
   */
  modeValueChanged(): void {
    // Stimulus runs this for the initial declaration before `connect()`, which renders
    // that declaration itself.
    if (!this.#connected) return;
    // A Value that already names the mode on screen is how the controller's own writes
    // come back; resolving it against storage again would undo a selection whose save
    // failed.
    if (this.modeValue === this.#published.mode) return;
    const stored = this.#readStored();
    if (stored !== null && this.modeValue !== stored) this.modeValue = stored;
    this.#applyTheme();
    this.#reconcileControls();
  }

  /**
   * Follows a `storageKey` rewritten after connect. A valid choice saved under the new
   * key outranks the mode on screen as at connect — `mode` is written to it and a moved
   * pair is reported as `reconcile` — and without one the mode stays. Nothing is copied
   * or written; the next selection is saved under the new key.
   */
  storageKeyValueChanged(): void {
    // `connect()` reads the initial key itself.
    if (!this.#connected) return;
    const key = this.storageKeyValue;
    if (key === this.#storageKeyRead) return;
    this.#storageKeyRead = key;
    const stored = this.#readStored();
    if (stored === null || stored === this.modeValue) return;
    this.modeValue = stored;
    this.#applyTheme();
    this.#reconcileControls();
  }

  /** Re-derives the single Tab stop and ARIA for an option set that changed. */
  optionTargetConnected(): void {
    if (this.#connected) this.#morph.schedule();
  }

  /** Re-derives them again when an option leaves, so a Tab stop always remains. */
  optionTargetDisconnected(): void {
    if (this.#connected) this.#morph.schedule();
  }

  /**
   * Selects the mode the activated option declares.
   *
   * Read through the same lane that decides which option is checked, so the two
   * can never disagree about what an option declares.
   */
  set(source: Event | HTMLElement): void {
    const { host, origin, reason } = actionSource(source);
    const option = host?.closest<HTMLElement>(targetSelector(this.identifier, "option"));
    if (!option || !this.optionTargets.includes(option)) return;
    if (origin && origin.closest(`[data-controller~="${this.identifier}"]`) !== this.element)
      return;
    const mode = this.#optionMode(option);
    if (mode) this.#setMode(mode, reason);
  }

  /** Toggles light↔dark for the 2-value single-button contract. */
  toggle(event?: Event): void {
    const mode = this.#readStored() ?? this.#mode;
    const dark = mode === "dark" || (mode === "system" && this.#media?.matches === true);
    this.#setMode(dark ? "light" : "dark", stateReasonFor(event));
  }

  /**
   * Central mode change: persist, apply to the root, resync controls, and report a real
   * move as `change`.
   */
  #setMode(mode: ThemeMode, reason: StateReason = "user"): void {
    this.modeValue = mode;
    this.#writeStored(mode);
    this.#commit(reason);
  }

  /**
   * Applies the current mode and reports it, but reports only a real move: the
   * event means "the selection or the effective theme moved", so re-choosing the
   * option already chosen is not one.
   */
  #commit(reason: StateReason): void {
    this.#applyTheme();
    this.#syncControls();
    const moved = this.#settle();
    if (moved) this.dispatch("change", { detail: { ...moved, reason } });
  }

  /**
   * Re-derives the controls from the mode on screen for a change the page made — a
   * `mode` declaration or an option set — and reports a pair that moved since the
   * last report as `reconcile`. An option coming or going moves neither half of the
   * pair, so of these changes only a declaration reports.
   */
  #reconcileControls(): void {
    this.#syncControls();
    const moved = this.#settle();
    if (moved) this.dispatch("reconcile", { detail: moved });
  }

  /**
   * Takes the pair on screen as the reported one and returns it when it differs
   * from the one reported before, or `null` when neither half moved.
   *
   * The baseline moves before anything is dispatched, so a listener that selects
   * another mode is measured from the pair it was just told about. What is
   * returned is a copy: the baseline has to survive a listener that writes to what
   * it was handed, or the next unchanged operation reads as a move.
   */
  #settle(): { mode: ThemeMode; resolved: ResolvedTheme } | null {
    const next = this.#current;
    const last = this.#published;
    this.#published = next;
    if (last.mode === next.mode && last.resolved === next.resolved) return null;
    return { ...next };
  }

  /** The pair the `change` and `reconcile` details carry, read from current state. */
  get #current(): { mode: ThemeMode; resolved: ResolvedTheme } {
    return { mode: this.#mode, resolved: this.#resolved() };
  }

  /**
   * Writes `data-theme` + `color-scheme` (the resolved theme) onto the target.
   *
   * @stimeoRenderRoot
   */
  #applyTheme(): void {
    const root = this.#targetElement();
    if (!root) return;
    const resolved = this.#resolved();
    root.setAttribute("data-theme", resolved);
    root.style.setProperty("color-scheme", resolved);
  }

  /**
   * Keeps the radiogroup (aria-checked + roving tabindex) or toggle (aria-pressed) in sync.
   *
   * @stimeoRenderRoot
   */
  #syncControls(): void {
    const options = this.optionTargets;
    if (options.length > 0) {
      const mode = this.#mode;
      let selected = -1;
      options.forEach((option, index) => {
        const isSelected = this.#optionMode(option) === mode;
        option.setAttribute("aria-checked", String(isSelected));
        if (isSelected && selected === -1) selected = index;
      });
      // APG: a radiogroup with no selection keeps its first radio tabbable.
      this.#roving.setActive(selected === -1 ? 0 : selected, { items: options });
      return;
    }
    // `aria-pressed` belongs to the 2-value contract, where the controller sits on
    // the button itself. A radiogroup whose options have not rendered yet is not
    // that, and must not be told it is a toggle.
    if (this.#isToggleButton) {
      this.element.setAttribute("aria-pressed", String(this.#resolved() === "dark"));
    }
  }

  /** Whether the controller element is the button of the 2-value contract. */
  get #isToggleButton(): boolean {
    return this.element.tagName === "BUTTON" || this.element.getAttribute("role") === "button";
  }

  /** The selected mode after validation; an unreadable declaration is the default. */
  get #mode(): ThemeMode {
    return isMode(this.modeValue) ? this.modeValue : DEFAULT_MODE;
  }

  /** The effective theme: the OS preference when `system`, else the mode itself. */
  #resolved(): ResolvedTheme {
    const mode = this.#mode;
    if (mode === "dark") return "dark";
    if (mode === "light") return "light";
    return this.#media?.matches ? "dark" : "light";
  }

  /** An option's mode from its `data-value`, or `null` when that is not one of the three. */
  #optionMode(option: HTMLElement): ThemeMode | null {
    const mode = option.getAttribute("data-value");
    return isMode(mode) ? mode : null;
  }

  /** Resolves the state-hook target (`<html>` by default). */
  #targetElement(): HTMLElement | null {
    const selector = this.#targetSelector;
    if (selector === DEFAULT_TARGET || selector === ":root") return document.documentElement;
    return document.querySelector<HTMLElement>(selector);
  }

  /** Reads a persisted, validated mode from `localStorage` (null when absent/blocked). */
  #readStored(): ThemeMode | null {
    const result = readLocalStorage(this.storageKeyValue);
    return result.ok && isMode(result.value) ? result.value : null;
  }

  /** Persists the mode, swallowing storage errors (private mode / quota). */
  #writeStored(mode: ThemeMode): void {
    writeLocalStorage(this.storageKeyValue, mode);
  }
}
